// src/components/appointment/AppointmentModal.jsx

import React, { useEffect } from "react";
import {
  X,
  CarFront,
  CalendarCheck,
  ShieldCheck,
} from "lucide-react";
import AppointmentForm from "./AppointmentForm";

const AppointmentModal = ({ open, onClose }) => {
  // =========================================================
  // LOCK BODY SCROLL WHEN MODAL IS OPEN
  // =========================================================
  useEffect(() => {
    if (!open) return;

    const originalOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [open]);

  // =========================================================
  // ESCAPE KEY
  // =========================================================
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="
        fixed
        inset-0
        z-[100]
        flex
        items-end
        justify-center
        bg-[#061A3A]/70
        p-0
        backdrop-blur-sm
        sm:items-center
        sm:p-4
        motion-safe:animate-[njvModalBackdrop_250ms_ease-out]
      "
      role="dialog"
      aria-modal="true"
      aria-labelledby="appointment-modal-title"
      aria-describedby="appointment-modal-description"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      {/* =====================================================
          MODAL
      ====================================================== */}
      <div
        className="
          relative
          flex
          max-h-[96dvh]
          w-full
          flex-col
          overflow-hidden
          rounded-t-[28px]
          border
          border-white/60
          bg-white
          shadow-[0_-10px_50px_rgba(6,26,58,0.20)]
          motion-safe:animate-[njvModalIn_350ms_cubic-bezier(0.22,1,0.36,1)]
          sm:max-h-[92vh]
          sm:max-w-2xl
          sm:rounded-[28px]
          sm:shadow-[0_25px_80px_rgba(6,26,58,0.25)]
        "
        onMouseDown={(event) => event.stopPropagation()}
      >
        {/* ===================================================
            MOBILE DRAG INDICATOR
        ==================================================== */}
        <div className="flex justify-center pt-3 sm:hidden">
          <span
            className="
              h-1
              w-10
              rounded-full
              bg-[#CBD5E1]
            "
          />
        </div>

        {/* ===================================================
            HEADER
        ==================================================== */}
        <div
          className="
            relative
            shrink-0
            overflow-hidden
            border-b
            border-[#E2E8E5]
            bg-gradient-to-br
            from-[#061A3A]
            via-[#0B2A4A]
            to-[#005543]
            px-5
            pb-5
            pt-4
            sm:px-6
            sm:pb-6
            sm:pt-5
          "
        >
          {/* Decorative glow */}
          <div
            className="
              pointer-events-none
              absolute
              -right-10
              -top-16
              h-40
              w-40
              rounded-full
              bg-[#00C878]/20
              blur-3xl
            "
          />

          <div
            className="
              pointer-events-none
              absolute
              -bottom-16
              -left-10
              h-36
              w-36
              rounded-full
              bg-[#8BF108]/10
              blur-3xl
            "
          />

          <div className="relative z-10 flex items-start justify-between gap-4">
            {/* Left */}
            <div className="flex min-w-0 items-start gap-3">
              {/* Icon */}
              <div
                className="
                  flex
                  h-11
                  w-11
                  shrink-0
                  items-center
                  justify-center
                  rounded-2xl
                  border
                  border-white/10
                  bg-white/10
                  text-[#8BF108]
                  shadow-inner
                  sm:h-12
                  sm:w-12
                "
              >
                <CarFront
                  className="h-5 w-5 sm:h-6 sm:w-6"
                  aria-hidden="true"
                />
              </div>

              <div className="min-w-0">
                {/* Eyebrow */}
                <div
                  className="
                    mb-1.5
                    flex
                    items-center
                    gap-2
                    text-[10px]
                    font-bold
                    uppercase
                    tracking-[0.16em]
                    text-[#8BF108]
                    sm:text-xs
                  "
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-[#8BF108]" />
                  NJV Motors
                </div>

                <h3
                  id="appointment-modal-title"
                  className="
                    text-xl
                    font-extrabold
                    tracking-tight
                    text-white
                    sm:text-2xl
                  "
                >
                  Book an Appointment
                </h3>

                <p
                  id="appointment-modal-description"
                  className="
                    mt-1
                    max-w-md
                    text-xs
                    leading-5
                    text-white/70
                    sm:text-sm
                  "
                >
                  Tell us what you need and our team will get back to you.
                </p>
              </div>
            </div>

            {/* Close */}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close appointment dialog"
              className="
                flex
                h-10
                w-10
                shrink-0
                items-center
                justify-center
                rounded-full
                border
                border-white/15
                bg-white/10
                text-white
                transition-all
                duration-200
                hover:bg-white/20
                hover:rotate-90
                focus:outline-none
                focus-visible:ring-2
                focus-visible:ring-[#8BF108]
                active:scale-95
              "
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* =================================================
              TRUST STRIP
          ================================================== */}
          <div
            className="
              relative
              z-10
              mt-5
              flex
              flex-wrap
              gap-x-4
              gap-y-2
              text-[10px]
              font-medium
              text-white/70
              sm:text-xs
            "
          >
            <div className="flex items-center gap-1.5">
              <CalendarCheck className="h-3.5 w-3.5 text-[#8BF108]" />
              Flexible booking
            </div>

            <div className="flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-[#8BF108]" />
              Trusted service
            </div>
          </div>
        </div>

        {/* ===================================================
            FORM AREA
        ==================================================== */}
        <div
          className="
            min-h-0
            flex-1
            overflow-y-auto
            overscroll-contain
            bg-[#F8FAF9]
            px-4
            py-5
            sm:px-6
            sm:py-6
          "
        >
          <div
            className="
              mx-auto
              w-full
              max-w-xl
            "
          >
            <AppointmentForm onCancel={onClose} />
          </div>
        </div>

        {/* ===================================================
            MOBILE SAFE AREA
        ==================================================== */}
        <div className="h-[env(safe-area-inset-bottom)] shrink-0 bg-[#F8FAF9] sm:hidden" />
      </div>
    </div>
  );
};

export default AppointmentModal;